import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import {
  ApiTypesEnum,
  CurrencyLockTypeEnum,
  DirectAssigmentDataInDTO,
  FindCarrierTypeEnum,
  GetPreviousDistributionInnerResponseOutDTO, PermissionTypesEnum,
  SelectedItemTypeEnum,
} from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { AiCarrierAutoSelectionDto } from '@components/ai/AiCarrierAutoSelection.tsx';
import CarrierDistributionSelector, {
  CarrierDistributionSelectorData,
  DistributionType,
  getSelectedUserIds,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';

interface ComponentProps {
  onContinue: () => void;
  customerId: string;
  applicationId: string;
  expeditionId?: string;
  aiCarrierAutoSelectionDto?: AiCarrierAutoSelectionDto;
}

interface FormData {
  distribution: CarrierDistributionSelectorData;
}

export const useCreateAssignCarrierSidebar = () => {
  const [visible, setVisible] = useState(false);

  const AssignCarrierSidebar: React.FC<ComponentProps> = ({ onContinue, customerId, applicationId, expeditionId, aiCarrierAutoSelectionDto }) => {
    const { tr } = useTranslationLgs();
    const { loggedUser } = useContext(AuthenticatedUserContext);
    const { cuSetDirectAssignment, getPreviousDistribution } = useContext(ApiContext);
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const [loading, setLoading] = useState(false);

    const [originalDistribution, setOriginalDistribution] = useState<CarrierDistributionSelectorData>(null);

    const originalDistributionAllowed = useSecuredFlag('assign-original-distribution', {
      kind: 'visibility',
      description: 'Load original distribution in assign-carrier sidebar',
      permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE_DISTRIBUTION_RW],
    }).granted;

    const getOriginalDistribution = (distribution?: GetPreviousDistributionInnerResponseOutDTO): CarrierDistributionSelectorData | null => {
      if (!distribution) return null;

      const fixedOrSpot = distribution.spot || distribution.fixed;
      const price = fixedOrSpot?.price || distribution.assignedPrice;

      const selectedItems = fixedOrSpot?.selectedItems?.map(item => ({
        id: item.id,
        userIds: item.userId ? [item.userId] : null,
        apiTypes: item.apiType ? [item.apiType as unknown as ApiTypesEnum] : null,
        carrierType: item.selectedItemType === SelectedItemTypeEnum.API
          ? FindCarrierTypeEnum.API
          : item.selectedItemType === SelectedItemTypeEnum.GROUP
            ? FindCarrierTypeEnum.GROUP
            : item.selectedItemType === SelectedItemTypeEnum.CARRIER && item.userId && item.userId.length > 0
              ? FindCarrierTypeEnum.CARRIER_USER
              : FindCarrierTypeEnum.CARRIER,
        _dachserProductTypeCode: fixedOrSpot.dachserProductType,
        _dachserDeliveryTermCode: fixedOrSpot.dachserDeliveryTerm,
        _schenkerProductTypeCode: fixedOrSpot.schenkerProductType,
        _schenkerAddonsTypeCodes: fixedOrSpot.schenkerAddonsType,
      })) || (distribution.assignedCarrierId ? [{ id: distribution.assignedCarrierId }] : []);

      return {
        distributionType: distribution.spot ? DistributionType.SPOT : DistributionType.FIXED,
        price,
        selectedItems,
        notifyAssignedCarrier: false,
        currencyLockType: distribution.currencyLockType || CurrencyLockTypeEnum.NONE,
        ladenFactor: distribution.ladenFactor > 0,
        ladenFactorValue: distribution.ladenFactor,
        allowPartialDelivery: distribution.allowPartialDelivery,
      };
    };

    useEffect(() => {
      if (visible && expeditionId && originalDistributionAllowed) {
        getPreviousDistribution(
          expeditionId,
          (data) => {
            if (data && data.previousDistribution) {
              setOriginalDistribution(getOriginalDistribution(data.previousDistribution));
            }
          },
        );
      }
    }, [expeditionId, visible, originalDistributionAllowed]);

    const validationRules = yup.object().shape({
      distribution: yup.object().shape({
        distributionType: yup.string(),
        price: yup.object().when('distributionType', {
          is: (type: string) => !!type, // pokud je jakýkoliv typ distribuce
          then: yup.object().shape({
            price: yup.number()
              .typeError(tr('AssignCarrierSidebar.priceShouldBeNumber', 'Price should be number'))
              .transform((value) => (isNaN(value) ? undefined : value))
              .nullable()
              .required(tr('AssignCarrierSidebar.mandatoryPrice', 'Price is required')),
            currency: yup.string().required(tr('CreateExpedition.mandatoryCurrency', 'Currency is required')),
          }).required(tr('AssignCarrierSidebar.mandatoryPrice', 'Price is required')),
          otherwise: yup.object().nullable(),
        }),
        selectedItems: yup.array().when('distributionType', {
          is: (type: string) => type === 'FIXED' || type === 'SPOT' || type === 'ASSIGN',
          then: yup.array()
            .min(1, tr('AssignCarrierSidebar.mandatoryCarriers', 'Please select at least one carrier'))
            .required(tr('AssignCarrierSidebar.mandatoryCarriers', 'Please select at least one carrier')),
          otherwise: yup.array().nullable(),
        }),
      }).nullable(),
    });

    const initDistribution = (): CarrierDistributionSelectorData => {
      return ({
        distributionType: DistributionType.ASSIGN,
        selectedItems: [],
        price: { currency: loggedUser?.defaultCurrencyIsoCode, price: 0 },
        notifyAssignedCarrier: false,
        currencyLockType: CurrencyLockTypeEnum.NONE,
      });
    };


    const form = useForm<FormData>(
      validationRules,
      { distribution: initDistribution() },
      (d) => onContinueHandler(d.distribution),
      false,
      false,
      () => {},
      'none'
    );

    const mapToDistributionInDTO = (data: CarrierDistributionSelectorData): DirectAssigmentDataInDTO => {
      return {
        carrierId: data.selectedItems[0].id,
        price: data.price?.price,
        currency: data.price?.currency,
        informCarrier: data.notifyAssignedCarrier,
        userIds: getSelectedUserIds(data.selectedItems),
      };
    };

    const onContinueHandler = (distribution: CarrierDistributionSelectorData) => {
      setLoading(true);
      cuSetDirectAssignment(
        customerId,
        applicationId,
        mapToDistributionInDTO(distribution),
        () => {
          setVisible(false);
          setLoading(false);
          onContinue();
        },
        null,
        {
          onValidationFailed: (errors) => {
            setLoading(false);
            setBeValidationErrors(errors);
          }
        }
      );
    };

    if (!visible) return null;

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          onContinue={() => {
            form.validateAndSend();
          }}
          heading={tr(`AssignCarrierSidebar.heading`, `Assign carrier directly`)}
          loading={loading}
          dataTestIdContinue={'carrierSelection-save'}
          dataTestIdBack={'carrierSelection-close'}>
          <>
            <BackendValidationComponent />
            <CarrierDistributionSelector
              allowedDistributionTypes={[DistributionType.ASSIGN]}
              dataTestIdPrefix={'carrierSelection'}
              distribution={form.values.distribution}
              originalDistribution={originalDistribution}
              onChange={v => {
                form.setFieldValue(form.names.distribution, v)
              }}
              validationErrors={form.validationErrors
                .filter(e => e.path?.startsWith('distribution'))
                .map(e => ({ path: e.path || '', message: e.message }))}
              aiCarrierAutoSelectionDto={aiCarrierAutoSelectionDto}
            />
          </>
        </RingilSidebar>
      </>);

  };

  return { AssignCarrierSidebar, setVisible };
};